import { createSelector } from 'reselect'
import { get, orderBy, sumBy, values, filter, map, isFinite, take, isEmpty, union } from 'lodash'
import moment from 'moment'

import { getDeviceSubtype } from './config'
import DeviceSubtypes from '../utils/deviceSubtypes'
import getMatchingRecords from '../utils/getMatchingRecords'

export const getPurgedAt = (state) => get(state, 'order.purgedAt', undefined)

export const getKioskPayment = (state) => get(state, 'order.kioskPayment', {})

export const getPurgableOrders = (state, msThreshold, options) => {
  const ordersById = get(state, 'order.byId', {})
  const conditionalAttribute = options?.force ? 'submittedAt' : 'syncedAt'
  const isPurgable = (order) => order?.createOrderParams?.state !== 'approved_offline' && moment().diff(moment(order[conditionalAttribute])) >= msThreshold
  const syncedOrdersIds = map(filter(values(ordersById), isPurgable), 'uuid')

  return syncedOrdersIds
}

export const getMaintenancedAt = (state) => {
  return state?.order?.maintenancedAt
}

export const getMaintenanceEnabled = (state) => {
  return (state?.order?.maintenanceEnabled === undefined) ? true : state?.order?.maintenanceEnabled
}

export const getSafeToPerformMaintenance = (state) => {
  const offlineLocalIds = state?.order?.offlineLocalIds ?? []
  const offlineCardIds = state?.order?.offlineCardIds ?? []
  const failureRemoteIds = state?.order?.failureRemoteIds ?? []

  return [offlineLocalIds, offlineCardIds, failureRemoteIds].every(isEmpty)
}

export const getOrders = (state) => {
  const orderFilter = get(state, 'order.ordersFilter') 
  const filterIsValid = ['successRemoteIds', 'offlineLocalIds', 'offlineCardIds', 'failureRemoteIds', 'unsyncableIds'].includes(orderFilter)
  const byId = get(state, 'order.byId', {})

  let orders = []
  if (orderFilter === 'offlineLocalIds') {
    const filteredIds = union(state?.order?.offlineLocalIds, state?.order?.offlineCardIds) 
    filteredIds?.forEach(id => orders?.push(byId[id]))
  } else {
    orders = values(byId)
  }

  const search = get(state, 'order.ordersSearch')
  if (search) {
    orders = orders?.filter(o => (o.orderNumber || '').toString().includes(search))
  }

  const sortedOrders = orderBy(orders, (o) => moment(o.submittedAt), ['desc'])
  const nonRefundable = []
  const refundable = []

  sortedOrders.forEach((order) => {
    const collection = order?.refundable ? refundable : nonRefundable
    collection.push(order)
  })

  return [...nonRefundable, ...refundable]
}

const getTabOrders = (state) => {
  const byId = get(state, 'order.byId', {})
  let tabOrders = values(byId)

  const sortedOrders = orderBy(tabOrders, (order) => moment(order?.submittedAt), ['desc'])
  const closed = []
  const open = []

  sortedOrders.forEach((order) => {
    const collection = order?.orderState === 'open' ? open : closed
    collection.push(order)
  })

  return [...open, ...closed]
}

export const makeGetTabOrders = () => createSelector(
  [getTabOrders],
  (tabOrders) => tabOrders
)

const getTabOrder = (state, orderId) => get(state, `order.byId.${orderId}`, {})

export const makeGetTabOrder = () => createSelector(
  getTabOrder,
  (tabOrder) => tabOrder
)

export const makeGetOrders = () => createSelector([getOrders], (orders) => orders)

export const getOrder = (state, id) => get(state, `order.byId.${id}`, {})

export const makeGetOrder = () => createSelector([getOrder], (order) => order)

export const getOrderCounts = (state) => {
  const orders = values(get(state, 'order.byId', {}))
  const failure = get(state, 'order.failureRemoteIds', [])
  const offline = get(state, 'order.offlineLocalIds', [])
  const offlineCardIds = get(state, 'order.offlineCardIds', [])
  const success = get(state, 'order.successRemoteIds', [])
  const byId = get(state, 'order.byId', {})
  const filterByDate = orders => {
    let syncedOrders = [];
    orders.forEach(id => {
      if (!isEmpty(byId[id])) {
        syncedOrders.push(byId[id])
      }
    })
    return syncedOrders.filter(order => moment(order?.syncedAt).isSame(moment(), "day"))
  }

  return {
    failure: filterByDate(failure).length,
    offline: offline.length + offlineCardIds.length,
    success: filterByDate(success).length,
  }
}

export const makeGetOrderCounts = () => createSelector([getOrderCounts], (counts) => counts)

export const getSelectedOrder = (state) => {
  const orderId = get(state, 'order.selectedOrderId')
  return get(state, `order.byId[${orderId}]`)
}

export const getUnsyncableIds = (state) => state?.order?.unsyncableIds ?? []

export const makeGetSelectedOrder = () => createSelector([getSelectedOrder], (order) => order)

export const getOfflineLocalIds = (state, limit) => {
  const orders = state?.order?.byId ?? {}
  const orderIds = state?.order?.offlineLocalIds ?? []

  if (!isFinite(limit)) {
    return orderIds
  } else {
    const matcher = (order) => (order?.uuid || order?.id) !== null && (order?.uuid || order?.id) !== undefined
    const result = getMatchingRecords(orderIds, orders, matcher, limit)

    return result
  }
}

export const makeGetOfflineLocalIds = () => createSelector([getOfflineLocalIds], (ids) => ids)

export const getOfflineCardIds = (state, limit) => {
  const orders = state?.order?.byId ?? {}
  const orderIds = state?.order?.offlineCardIds ?? []

  if (!isFinite(limit)) {
    return orderIds
  } else {
    const matcher = (order) => (order?.uuid || order?.id) !== null && (order?.uuid || order?.id) !== undefined
    const result = getMatchingRecords(orderIds, orders, matcher, limit)

    return result
  }
}

export const makeGetOfflineCardIds = () => createSelector([getOfflineCardIds], (ids) => ids)

export const getAllRevenueInCents = (state) => {
  const orders = values(get(state, 'order.byId', {}))
  return sumBy(orders, 'amountInCents')
}

export const makeGetAllRevenueInCents = () => createSelector([getAllRevenueInCents], (cents) => cents)

export const getStoredRickCheckoutOrderUuid = state => get(state, 'order.storedOrderUuid', '')

export const getStoredRickCheckoutOrderNumber = state => get(state, 'order.storedOrderNumber', '')

export const makeGetServiceChargeLegalText = () => createSelector(
  makeGetOrder(),
  state => state,
  (order, state) => {
    const menuUuid = order?.standMenuUuid
    const standUuid = state?.menus?.byId?.[menuUuid]?.standUuid
    const stand = state?.stands?.byId?.[standUuid]

    return stand?.venue?.serviceChargeLegal
  }
)

export const getOrderInProgress = (state) => state?.order?.orderInProgress ?? {}

